import { DollarSign, ArrowLeft } from 'lucide-react'
import { cn } from "@/lib/utils"

const getDropdownClasses = (showAbove?: boolean) => cn(
    "absolute z-[10001] w-full bg-background border rounded-lg shadow-lg max-h-[300px] overflow-hidden flex flex-col",
    showAbove ? "bottom-full mb-1" : "top-full mt-1"
)
import { Button } from '@/components/ui/button'
import { IdResolutionHint } from './IdResolutionHint'
import { formatPrice, formatBillingInterval } from './utils'

interface Price {
    id: string
    currencyOptions: Record<string, { amount: number }>
    recurring: boolean
    interval: string | null
    intervalCount: number | null
}

interface PriceDropdownProps {
    prices: Price[]
    onSelect: (price: Price) => void
    onBack: () => void
    showAbove?: boolean
    /** Explains how a pasted id got the picker to this step. */
    notice?: string | null
}

const DropdownHeader = ({
    title,
    onBack,
    notice,
}: {
    title: string
    onBack: () => void
    notice?: string | null
}) => {
    return (
        <div className="sticky top-0 bg-background border-b px-4 py-3 z-10">
            <div className="flex items-center gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        onBack()
                    }}
                    className="h-8 w-8 p-0"
                >
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            </div>
            {notice && <IdResolutionHint message={notice} className="mt-2" />}
        </div>
    )
}

export const PriceDropdown = ({ prices, onSelect, onBack, showAbove, notice }: PriceDropdownProps) => {
    if (prices.length === 0) {
        return (
            <div className={getDropdownClasses(showAbove)}>
                <DropdownHeader title="Select a Price" onBack={onBack} notice={notice} />
                <div className="flex items-center justify-center p-4 text-muted-foreground text-sm">
                    No prices available for this variant
                </div>
            </div>
        )
    }

    return (
        <div className={getDropdownClasses(showAbove)}>
            <DropdownHeader title="Select a Price" onBack={onBack} notice={notice} />
            <div className="overflow-y-auto">
                {prices.map((price) => (
                    <div
                        key={price.id}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            onSelect(price)
                        }}
                        className={cn(
                            'flex items-center gap-3 p-3 cursor-pointer transition-colors',
                            'hover:bg-accent border-b last:border-b-0',
                        )}
                    >
                        <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium">{formatPrice(price.currencyOptions)}</p>
                            <p className="text-sm text-muted-foreground">
                                {formatBillingInterval(price.recurring, price.interval, price.intervalCount)}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

